<!-- assets/components/Admin/SubscriptionView.vue -->

<template>
  <Paper>
    <div class="subscription-options" v-if="user.subscription">
      <div class="subscription-option">
        <div class="subscription-option-block option-pricing option-visible">
          <div class="subscription-option-left">
            <h3>{{ user.subscription.plan.name }}</h3>
            <span class="subscription-option-price">{{ subscriptionTotal() | priceFormat }} {{
                period(user.subscription.plan)
              }}</span>
            <p v-if="user.subscription.activated">Your subscription is active.</p>
          </div>
        </div>
      </div>
      <div
          class="subscription-option"
          v-for="as in user.subscription_additional_services">
        <div class="subscription-option-block option-pricing option-visible">
          <div class="subscription-option-left">
            <h3>{{ as.name }}</h3>
            <span class="subscription-option-price">${{ as.base_price }} {{ period(as) }}</span>
          </div>
          <div class="subscription-option-right">
            <div class="subscription-option-description">{{ as.description }}</div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="user && user.subscription && !user.subscription.activated">
      <p class="my-2">Enter your billing information to activate your subscription.</p>
      <form @submit.prevent="subscribe">
        <fieldset class="payment max-w-screen-md border-0 m-0 py-4">
          <legend class="text-cal-light-blue font-bold p-0 m-0 uppercase text-base">Payment</legend>
          <div class="space-y-3">
            <div class="flex flex-row gap-2">
              <div class="flex-grow">
                <label for="billing_first_name" class="sr-only"><span>Billing first name</span></label>
                <input required id="billing_first_name" type="text" autocomplete="given-name"
                       v-model="bill_to.first_name"
                       placeholder="First name" class="block w-full sm:text-sm border border-gray-300 p-2"/>
              </div>
              <div class="flex-grow">
                <label for="billing_last_name" class="sr-only"><span>Billing last name</span></label>
                <input required id="billing_last_name" type="text" autocomplete="family-name"
                       v-model="bill_to.last_name"
                       placeholder="Last name" class="block w-full sm:text-sm border border-gray-300 p-2"/>
              </div>
            </div>
            <div class="">
              <label class="sr-only" for="address1">Address 1</label>
              <input required type="text" v-model="bill_to.address" id="address1" placeholder="Address 1"
                     class="block w-full sm:text-sm border border-gray-300 p-2"/>
            </div>
            <div class="">
              <label class="sr-only" for="address2">Address 2</label>
              <input type="text" v-model="bill_to.address2" id="address2" placeholder="Address 2"
                     class="block w-full sm:text-sm border border-gray-300 p-2"/>
            </div>
            <div class="">
              <label class="sr-only" for="billing_city">City</label>
              <input required type="text" v-model="bill_to.city" id="billing_city" placeholder="City"
                     class="block w-full sm:text-sm border border-gray-300 p-2"/>
            </div>
            <div class="flex flex-row gap-2">
              <div class="flex-grow">
                <label class="sr-only" for="billing_state">State</label>
                <div class="relative w-full">
                  <select class="block w-full appearance-none sm:text-sm border border-gray-300 pl-2 pr-8 py-2" required
                          id="billing_state" v-model="bill_to.state">
                    <option value="" disabled>Select a state</option>
                    <option v-for="state in options.states" :value="state.abbr">{{ state.name }}</option>
                  </select>
                  <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-3" fill="none" viewBox="0 0 24 24"
                         stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
                    </svg>
                  </div>
                </div>
              </div>
              <div class="">
                <label class="sr-only" for="billing_zip">Zip</label>
                <input required type="text" v-model="bill_to.zip" id="billing_zip" placeholder="Zip code"
                       class="block w-full sm:text-sm border border-gray-300 p-2"/>
              </div>
            </div>
            <hr class="border border-gray-300"/>
            <div class="">
              <label class="sr-only" for="billing_cc">Card number</label>
              <input required type="text" pattern="[0-9]*" inputmode="numeric" id="billing_cc" placeholder="Card Number"
                     v-model="card.card_number" autocomplete="cc-number"
                     class="block w-full sm:text-sm border border-gray-300 p-2"/>
            </div>
            <div class="flex flex-row gap-2">
              <div class="flex-grow">
                <label class="sr-only" for="billing_exp">Expiration (mm/yy)</label>
                <input required type="text" pattern="(?:0[1-9]|1[0-2])/[0-9]{2}" inputmode="numeric" id="billing_exp"
                       placeholder="Expiration (mm/yy)"
                       v-model="expiration_date" autocomplete="cc-exp"
                       class="block w-full sm:text-sm border border-gray-300 p-2"/>
              </div>
              <div class="flex-grow">
                <label class="sr-only" for="billing_cvv">CVV</label>
                <input required type="text" pattern="[0-9]*" inputmode="numeric" id="billing_cvv" placeholder="CVV"
                       v-model="card.cvv" autocomplete="cc-csc"
                       class="block w-full sm:text-sm border border-gray-300 p-2"/>
              </div>
            </div>
          </div>
        </fieldset>
        <hr class="border border-gray-300"/>
        <div>
          <label class="sr-only" for="promo_code">Promotional code</label>
          <input type="text" v-model="promo_code" id="promo_code" placeholder="Promo code" class="block w-full sm:text-sm border border-gray-300 p-2"/>
        </div>
        <div class="text-sm my-2">
          <label for="terms_of_service_agreement">
            <input v-model="agreedToTerms" type="checkbox" id="terms_of_service_agreement" required/>
            I have read and agree to the <a href="https://constructalead.com/terms-of-service/" target="_blank">terms of
            service</a>.
          </label>
        </div>
        <div class="details__inner">
          <div v-if="error">
            <p class="error-message">{{ errorMessage }}</p>
          </div>
          <div class="my-2">
            <button :disabled="!agreedToTerms" :class="{ 'cursor-not-allowed opacity-70': !agreedToTerms }" class="bg-cal-gold px-6 py-2 text-cal-blue uppercase border-0 font-bold rounded-sm shadow-md">
              Activate subscription
            </button>
          </div>
        </div>
      </form>
    </div>
  </Paper>
</template>

<script>

import Paper from '../Layout/Paper.vue'
import ColumnView from '../Shared/ColumnView.vue';
import VueButton from '../Shared/VueButton.vue';

import WebServices from '../../services/WebServices';

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

export default {

  components: {Paper, VueButton, ColumnView},

  data() {
    return {
      userServices: WebServices("Users"),
      subscriptionsServices: WebServices("Users/Me/Subscription"),
      projectServices: WebServices("Projects"),
      user: {},
      card: {
        card_number: '',
        expiration_date: '',
        cvv: '',
      },
      bill_to: {
        first_name: '',
        last_name: '',
        address: '',
        address2: '',
        state: '',
        country: 'US',
        zip: '',
      },
      projectTypes: [],
      states: [],
      options: {
        states: [],
        projectTypes: [],
      },
      promo_code: '',
      error: false,
      errorMessage: '',
      agreedToTerms: false
    }
  },

  created() {
    this.get();
  },

  methods: {
    mask(value, limit, separator) {
      const output = [];
      for (let i = 0; i < value.length; i++) {
        if (i !== 0 && i % limit === 0) {
          output.push(separator);
        }
        output.push(value[i]);
      }
      return output.join("");
    },
    subscribe() {
      const subscriber = {
        "plan": this.user.subscription.plan.id,
        "bill_to": this.bill_to,
        "card": this.card,
        "additional_services": this.user.subscription_additional_services.map(item => item.id),
        "plan_verticals": this.user.subscription_project_types.map(item => item.id),
        "plan_states": this.user.subscription_states.map(item => item.id),
        promo_code: this.promo_code
      };

      this.subscriptionsServices
          .update(subscriber)
          .then(() => {
            // Reload the user.
            this.$root.$data.auth.getUserInfo();
            swal({
              title: "Success!",
              text: "Your subscription has been updated successfully.",
              type: "success",
              confirmButtonColor: "#40B572",
              timer: null
            }, function () {
              window.scrollTo(0, 0)
              this.$router.push({name: "projects"});
              this.$emit("subscription-changed");
              swal.close();
            }.bind(this));
          }, (response) => {
            console.log(response)
            swal({
              title: "Error",
              text: response.body.message,
              type: "error",
              confirmButtonColor: "#40B572",
              timer: null
            }, function () {
              swal.close();
            }.bind(this));
          });
    },
    get() {
      WebServices("Projects")
          .get({}, 'AdvancedOptions')
          .then(function (response) {

            const data = response.data.data;
            this.options.states = data.states;
          }.bind(this));
      this.userServices
          .get({withSettings: true}, 'Me')
          .then(function (response) {
            this.user = response.data.data
            this.bill_to.first_name = this.user.first_name;
            this.bill_to.last_name = this.user.last_name;
            this.bill_to.address = this.user.address;
            this.bill_to.address2 = this.user.address2;
            this.bill_to.state = this.user.state;
            this.bill_to.country = this.user.country;
            this.bill_to.zip = this.user.zip;
          }.bind(this));
    },

    period(plan) {
      return plan.months === 12 ? "/ year" : "/ 6 months";
    },

    subscriptionTotal() {
      if (this.user.subscription.payment_total) {
        return parseFloat(this.user.subscription.payment_total).toFixed(2);
      } else {
        // @todo full math.
        return this.user.subscription.plan.base_price;
      }
    },

  },
  filters: {
    priceFormat(value) {
      if (isNaN(value)) {
        return '';
      }
      return value ? formatter.format(value).replace('.00', '') : ''
    }
  },
  computed: {
    expiration_date: {
      get() {
        return this.card.expiration_date;
      },
      set(value) {
        let newValue = value;
        newValue = newValue.replace(/\D/g, '');
        if (newValue.match(/^\d{0,4}$/g)) {
          newValue = this.mask(newValue, 2, '/');
          this.card.expiration_date = newValue;
        } else {
          this.card.expiration_date = value;
        }
      }
    }
  }
}

</script>
